import { useEffect, useState } from "react";
import { checkAndSendSignals, TASignal } from "../services/signalService";
import { isTelegramConfigured } from "../services/telegramService";

// Edit this list to watch specific symbols (crypto + stocks)
const DEFAULT_WATCHLIST = ["BTC/USDT", "ETH/USDT", "MSTR"];

const CHECK_INTERVAL_MS = 15 * 60 * 1000; // 15 min

export default function TASignalMonitor({ watchlist = DEFAULT_WATCHLIST }: { watchlist?: string[] }) {
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [lastSignals, setLastSignals] = useState<TASignal[]>([]);
  const [lastSent, setLastSent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const { sent, signals } = await checkAndSendSignals(watchlist, "1h");
      setLastCheck(new Date());
      setLastSignals(signals);
      setLastSent(sent);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Check failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runCheck();
    const id = setInterval(runCheck, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [watchlist.join(",")]);

  const hasTelegram = isTelegramConfigured();

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-white">📈 TA Buy/Sell Signals</h3>
        <div className="flex gap-2">
          <button
            onClick={runCheck}
            disabled={loading}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Checking..." : "Check Now"}
          </button>
        </div>
      </div>

      <p className="text-sm text-gray-400 mb-3">
        Watching: {watchlist.join(", ")} — RSI (≤30 buy, ≥70 sell)
      </p>

      {!hasTelegram && (
        <p className="text-amber-400 text-sm mb-3">
          Add REACT_APP_TELEGRAM_BOT_TOKEN and REACT_APP_TELEGRAM_CHAT_ID to receive signals
        </p>
      )}

      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

      {lastCheck && (
        <p className="text-xs text-gray-500 mb-3">
          Last check: {lastCheck.toLocaleTimeString()} — {lastSent} sent to Telegram
        </p>
      )}

      {lastSignals.length > 0 ? (
        <div className="space-y-2">
          {lastSignals.map((s, i) => (
            <div
              key={i}
              className={`p-3 rounded-lg text-sm ${
                s.type === "BUY" ? "bg-green-900/40 border border-green-700" : "bg-red-900/40 border border-red-700"
              }`}
            >
              <span className="font-semibold">{s.type === "BUY" ? "🟢 BUY" : "🔴 SELL"}</span> {s.symbol} — {s.reason}
              {s.price != null && (
                <span className="text-gray-400 ml-2">${s.price >= 1000 ? s.price.toLocaleString(undefined, { maximumFractionDigits: 0 }) : s.price.toFixed(2)}</span>
              )}
            </div>
          ))}
        </div>
      ) : lastCheck && !loading && (
        <p className="text-gray-500 text-sm">No signals at last check. RSI within normal range.</p>
      )}
    </div>
  );
}
